<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载代聊会话">
    <div class="sc-page">
      <div class="page-head">
        <div class="title-block">
          <h2>代聊会话</h2>
          <p>这里是别人授权给你查看 / 代聊的伴侣会话。代聊消息会标记为「分享者代聊」，并经微信网关转发。</p>
        </div>
      </div>

      <div class="layout">
        <aside class="list">
          <div v-if="!convs.length" class="sc-empty">暂无被授权的代聊会话</div>
          <div
            v-for="c in convs"
            :key="c.id"
            class="conv"
            :class="{ active: c.id === active.id }"
            @click="select(c)"
          >
            <img src="@/assert/主页人物.png" alt="" class="av" />
            <div class="info">
              <div class="top">
                <b>{{ c.companionName }}</b>
                <em>{{ c.lastTime }}</em>
              </div>
              <p>{{ c.lastMessage }}</p>
              <div class="meta">
                <span class="from">{{ c.fromUser }}</span>
                <span v-if="c.canTakeover" class="badge ok">可代聊</span>
                <span v-else class="badge">仅查看</span>
              </div>
            </div>
            <span v-if="c.unread" class="unread">{{ c.unread }}</span>
          </div>
        </aside>

        <section class="chat" v-if="active.id">
          <header class="chat-head">
            <img src="@/assert/主页人物.png" alt="" class="av" />
            <div class="meta">
              <b>{{ active.companionName }}</b>
              <p>对方：{{ active.fromUser }} · {{ active.canTakeover ? "已授权代聊" : "仅可查看" }}</p>
            </div>
          </header>
          <div class="messages">
            <div v-if="messageLoading" class="conversation-loading"><i class="el-icon-loading"></i> 正在加载消息</div>
            <div v-for="m in active.messages" :key="m.id" class="row" :class="m.role">
              <img v-if="m.role !== 'peer'" src="@/assert/主页人物.png" alt="" class="av" />
              <div class="wrap">
                <span v-if="m.role === 'takeover'" class="actor"><i class="el-icon-user"></i> {{ m.actor }}</span>
                <div class="bubble">{{ m.text }}</div>
              </div>
            </div>
          </div>
          <footer class="composer" v-if="active.canTakeover">
            <el-input v-model="draft" type="textarea" :rows="2" resize="none" placeholder="作为分享者代替伴侣回复…（将经微信网关转发）" />
            <el-button type="primary" round class="send" @click="sendTakeover">代聊发送</el-button>
          </footer>
          <footer class="composer locked" v-else>
            <i class="el-icon-lock"></i> 对方未授权代聊，你只能查看消息
          </footer>
        </section>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { sharedConvApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "SharedConversationsView",
  components: { DashboardLayout },
  data() {
    return {
      convs: [],
      active: {},
      draft: "",
      initialLoading: true,
      messageLoading: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const list = await sharedConvApi.list();
        this.convs = (list || []).map((g) => ({
          id: g.id,
          companionId: g.companion_id,
          companionName: g.companion_name || "伴侣 #" + g.companion_id,
          fromUser: "用户 #" + g.grantor_user_id,
          canTakeover: g.can_takeover_reply,
          lastMessage: "",
          lastTime: "",
          unread: 0,
          messages: [],
        }));
        if (this.convs.length) await this.select(this.convs[0]);
      } catch (e) {
        this.$message.warning("加载代聊会话失败");
      } finally {
        this.initialLoading = false;
      }
    },
    async select(c) {
      this.active = c;
      this.messageLoading = true;
      c.messages = [];
      try {
        const msgs = await sharedConvApi.messages(c.id);
        c.messages = (msgs || []).map((m, i) => ({
          id: i,
          role: m.role === "user" ? "peer" : "ai",
          text: m.content,
        }));
        const last = c.messages[c.messages.length - 1];
        if (last) c.lastMessage = last.text;
      } catch (e) {
        this.$message.warning("加载消息失败");
      } finally {
        this.messageLoading = false;
      }
    },
    async sendTakeover() {
      const text = this.draft.trim();
      if (!text) return;
      try {
        await sharedConvApi.send(this.active.id, text);
        this.active.messages.push({ id: Date.now(), role: "takeover", text, actor: "分享者代聊" });
        this.active.lastMessage = text;
        this.active.lastTime = "刚刚";
        this.draft = "";
        this.$message.success("已通过微信网关转发");
      } catch (e) {
        this.$message.error(e.message || "转发失败");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sc-page {
  min-width: 0;

  .page-head {
    margin-bottom: 20px;

    .title-block {
      h2 {
        color: var(--xyo-ink-strong);
      }

      p {
        max-width: 720px;
        color: var(--xyo-muted);
      }
    }
  }

  .layout {
    display: grid;
    grid-template-columns: 290px minmax(0, 1fr);
    gap: 14px;
    height: calc(100vh - 174px);
    min-height: 560px;
    max-height: 880px;

    .list {
      min-width: 0;
      padding: 8px;
      border: 1px solid var(--xyo-line);
      border-radius: var(--xyo-radius-lg);
      background: rgba(252, 251, 253, 0.72);
      overflow-y: auto;
      scrollbar-color: var(--xyo-line-strong) transparent;
      scrollbar-width: thin;

      .sc-empty {
        display: grid;
        min-height: 160px;
        padding: 24px 12px;
        place-items: center;
        text-align: center;
        color: var(--xyo-muted);
        font-size: 13px;
      }

      .conv {
        position: relative;
        display: flex;
        align-items: flex-start;
        min-width: 0;
        padding: 11px 10px;
        margin-bottom: 2px;
        border: 1px solid transparent;
        border-radius: 12px;
        cursor: pointer;
        transition: border-color 180ms ease, background-color 180ms ease, transform 180ms ease;

        &::before {
          position: absolute;
          top: 14px;
          bottom: 14px;
          left: -1px;
          width: 3px;
          border-radius: 0 4px 4px 0;
          background: var(--xyo-primary);
          content: "";
          opacity: 0;
          transform: scaleY(0.5);
          transition: opacity 180ms ease, transform 180ms ease;
        }

        &:hover {
          border-color: var(--xyo-line);
          background: var(--xyo-surface-raised);
        }

        &.active {
          border-color: #ead4c2;
          background: var(--xyo-accent-pale);

          &::before {
            opacity: 1;
            transform: scaleY(1);
          }
        }

        .av {
          width: 40px;
          height: 40px;
          margin-right: 9px;
          flex-shrink: 0;
          border-radius: 50%;
          object-fit: cover;
          object-position: center 18%;
          border: 2px solid rgba(255, 255, 255, 0.92);
          background: var(--xyo-canvas-deep);
          box-shadow: 0 0 0 1px var(--xyo-line);
        }

        .info {
          flex: 1;
          min-width: 0;

          .top {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 4px;

            b {
              min-width: 0;
              overflow: hidden;
              color: var(--xyo-ink-strong);
              font-size: 14px;
              font-weight: 700;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            em {
              flex-shrink: 0;
              color: #9a8d81;
              font-size: 11px;
              font-style: normal;
            }
          }

          p {
            margin: 0 0 6px;
            overflow: hidden;
            color: var(--xyo-muted);
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .meta {
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;

            .from {
              min-width: 0;
              overflow: hidden;
              color: #97897d;
              font-size: 11px;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .badge {
              flex-shrink: 0;
              padding: 2px 7px;
              border: 1px solid var(--xyo-line);
              border-radius: 999px;
              color: var(--xyo-muted);
              font-size: 10px;
              line-height: 1.25;
              background: var(--xyo-surface-muted);

              &.ok {
                border-color: #e4bda7;
                color: var(--xyo-primary);
                background: var(--xyo-accent-pale);
              }
            }
          }
        }

        .unread {
          position: absolute;
          top: 8px;
          left: 39px;
          min-width: 17px;
          height: 17px;
          padding: 0 5px;
          border: 2px solid var(--xyo-surface-raised);
          border-radius: 999px;
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          line-height: 13px;
          text-align: center;
          background: var(--xyo-primary);
        }
      }
    }

    .chat {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      border: 1px solid var(--xyo-line);
      border-radius: var(--xyo-radius-lg);
      background: var(--xyo-surface-raised);
      overflow: hidden;
      box-shadow: var(--xyo-shadow-sm);

      .chat-head {
        display: flex;
        align-items: center;
        min-width: 0;
        min-height: 70px;
        padding: 13px 18px;
        border-bottom: 1px solid var(--xyo-line);
        background: rgba(255, 255, 255, 0.94);

        .av {
          width: 42px;
          height: 42px;
          margin-right: 11px;
          flex-shrink: 0;
          border-radius: 50%;
          object-fit: cover;
          object-position: center 18%;
          border: 2px solid var(--xyo-surface-raised);
          background: var(--xyo-canvas-deep);
          box-shadow: 0 0 0 1px var(--xyo-line-strong);
        }

        .meta {
          min-width: 0;

          b {
            display: block;
            overflow: hidden;
            color: var(--xyo-ink-strong);
            font-size: 16px;
            font-weight: 720;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          p {
            margin: 3px 0 0;
            overflow: hidden;
            color: var(--xyo-muted);
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }

      .messages {
        flex: 1;
        min-height: 0;
        padding: 24px clamp(18px, 3vw, 38px);
        overflow-y: auto;
        background: var(--xyo-surface-muted);
        scrollbar-color: var(--xyo-line-strong) transparent;
        scrollbar-width: thin;

        .row {
          display: flex;
          align-items: flex-end;
          min-width: 0;
          margin-bottom: 16px;

          .av {
            width: 32px;
            height: 32px;
            flex-shrink: 0;
            border-radius: 50%;
            object-fit: cover;
            object-position: center 18%;
            border: 2px solid var(--xyo-surface-raised);
            background: var(--xyo-canvas-deep);
            box-shadow: 0 0 0 1px var(--xyo-line);
          }

          .wrap {
            min-width: 0;
            max-width: min(72%, 680px);
            margin: 0 9px;

            .actor {
              display: block;
              margin: 0 3px 5px;
              color: var(--xyo-primary);
              font-size: 11px;
              font-weight: 600;
              text-align: right;

              i {
                margin-right: 2px;
              }
            }

            .bubble {
              padding: 10px 14px;
              overflow-wrap: anywhere;
              border: 1px solid transparent;
              border-radius: 15px;
              color: var(--xyo-ink);
              font-size: 14px;
              line-height: 1.6;
              transition: transform 180ms ease, box-shadow 180ms ease;

              &:hover {
                transform: translateY(-1px);
              }
            }
          }

          &.peer {
            justify-content: flex-start;

            .bubble {
              border-color: var(--xyo-line);
              border-bottom-left-radius: 5px;
              background: var(--xyo-surface-raised);
              box-shadow: 0 5px 16px rgba(34, 28, 48, 0.05);
            }
          }

          &.ai {
            flex-direction: row-reverse;

            .bubble {
              color: #fff;
              border-color: #493328;
              border-bottom-right-radius: 5px;
              background: var(--xyo-ink);
              box-shadow: 0 7px 18px rgba(34, 28, 48, 0.12);
            }
          }

          &.takeover {
            flex-direction: row-reverse;

            .bubble {
              border-color: #e3b99f;
              border-bottom-right-radius: 5px;
              color: var(--xyo-ink-strong);
              background: var(--xyo-accent-soft);
              box-shadow: 0 6px 16px rgba(201, 66, 37, 0.08);
            }
          }
        }
      }

      .composer {
        position: relative;
        flex-shrink: 0;
        padding: 14px 142px 14px 18px;
        border-top: 1px solid var(--xyo-line);
        background: var(--xyo-surface-raised);
        transition: background-color 180ms ease;

        &:focus-within {
          background: #fff;
        }

        ::v-deep .el-textarea__inner {
          min-height: 64px !important;
          padding: 10px 12px;
          border-color: var(--xyo-line) !important;
          color: var(--xyo-ink-strong) !important;
          background: var(--xyo-surface-muted) !important;
          box-shadow: none !important;

          &:focus {
            border-color: var(--xyo-accent) !important;
            background: #fff !important;
            box-shadow: var(--xyo-focus) !important;
          }
        }

        .send {
          position: absolute;
          right: 18px;
          bottom: 25px;
          min-width: 108px;
          border-color: var(--xyo-primary) !important;
          color: #fff !important;
          background: var(--xyo-primary) !important;

          &:hover,
          &:focus {
            border-color: var(--xyo-primary-hover) !important;
            background: var(--xyo-primary-hover) !important;
          }
        }

        &.locked {
          padding: 18px 20px;
          color: var(--xyo-muted);
          font-size: 13px;
          text-align: center;
          background: var(--xyo-surface-muted);

          i {
            margin-right: 5px;
            color: var(--xyo-primary);
          }
        }
      }
    }
  }

  @media (max-width: 1100px) {
    .layout {
      grid-template-columns: 260px minmax(0, 1fr);
    }
  }

  @media (max-width: 768px) {
    .layout {
      display: flex;
      flex-direction: column;
      gap: 12px;
      height: auto;
      min-height: 0;
      max-height: none;

      .list {
        order: 2;
        width: 100%;
        max-height: 280px;
        overflow-x: hidden;
      }

      .chat {
        order: 1;
        width: 100%;
        min-height: min(620px, calc(100dvh - 136px));

        .chat-head {
          padding: 12px 14px;
        }

        .messages {
          min-height: 340px;
          padding: 18px 12px;

          .row {
            margin-bottom: 14px;

            .wrap {
              max-width: calc(100% - 42px);

              .bubble {
                padding: 9px 12px;
                font-size: 13px;
              }
            }
          }
        }

        .composer {
          padding: 12px;

          ::v-deep .el-textarea__inner {
            min-height: 72px !important;
          }

          .send {
            position: static;
            width: 100%;
            margin-top: 10px;
          }

          &.locked {
            padding: 16px 12px;
          }
        }
      }
    }
  }

  @media (max-width: 420px) {
    .layout .chat {
      border-radius: 14px;

      .chat-head .meta p {
        max-width: calc(100vw - 112px);
      }

      .messages .row {
        .av {
          width: 28px;
          height: 28px;
        }

        .wrap {
          max-width: calc(100% - 34px);
          margin: 0 6px;
        }
      }
    }
  }
}

.conversation-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  gap: 8px;
  color: var(--xyo-muted, #8c8493);
  font-size: 13px;

  i {
    color: var(--xyo-primary, #c94225);
    font-size: 18px;
  }
}
</style>
